import { ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { NovaSpinner } from "@nova/component-nova-spinner"
import { AddOns, Licenses, SubscriptionsType, ID } from "@nova/constants"
import { useRoutingParameter } from "panels/routing/useRoutingId"
import { useCallback, useEffect, useMemo, useState } from "react"
import { RadioInput } from "@nova/component-radio-input"
import { formatSubscriptionType } from "../module"
import { CheckboxInput } from "@nova/component-checkbox-input"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectUserID } from "user/profile/selectors"
import { assignPurchasedLicense } from "features/entity-caching/licenses/thunks"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { useTranslation } from "@nova/use-translation"
import * as Icons from "@nova/component-icons"
import { useAsyncCallback } from "@nova/use-async-callback"
import { hideGlobalModal } from "runlevel/actions"

const clearQueryParam = (param: string) => {
    const url = new URL(window.location.href)
    url.searchParams.delete(param)
    window.history.replaceState(null, "", url.toString())
}

export const PaymentSuccessModal = () => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const checkoutSessionId = useRoutingParameter("session_id")

    const userId = useAppSelector(selectUserID)

    const [isLoading, setIsLoading] = useState(true)
    const [licensesPurchased, setLicensesPurchased] = useState<
        Record<Licenses, boolean> | undefined
    >(undefined)
    const [addOnsPurchased, setAddOnsPurchased] = useState<Record<AddOns, boolean> | undefined>(
        undefined
    )

    const [selectedLicense, setSelectedLicense] = useState<string | "none">("none")
    const [selectedAddOns, setSelectedAddOns] = useState<Partial<Record<AddOns, boolean>>>({})
    const [subscriptionId, setSubscriptionId] = useState<ID>()

    useEffect(() => {
        dispatch(
            setGlobalNotificationContext({
                message: "Purchase Confirmed!",
                status: GlobalNotificationStatus.success,
                timeoutInMs: 5000,
            })
        )
    }, [dispatch])

    useEffect(() => {
        if (!checkoutSessionId) return
        const fetchCheckoutDetails = async () => {
            const res = await fetch(
                `/api/stripe/stripe-get-checkout-details?sessionId=${checkoutSessionId}`
            )
            const body = await res.json()
            const licensesPurchased = body.licensesPurchased
            const addOnsPurchased = body.addOnsPurchased
            setLicensesPurchased(licensesPurchased)
            setAddOnsPurchased(addOnsPurchased)
            setSubscriptionId(body.subscriptionId)

            if (licensesPurchased)
                setSelectedLicense(Object.keys(licensesPurchased).at(-1) || "none")
            if (addOnsPurchased) setSelectedAddOns(addOnsPurchased)
            setIsLoading(false)
        }
        fetchCheckoutDetails()
    }, [checkoutSessionId])

    const closeModal = useCallback(() => {
        clearQueryParam("session_id")
        dispatch(hideGlobalModal())
    }, [dispatch])

    const { handleConfirm, loading: confirmLoading } = useAsyncCallback(
        {
            handleConfirm: async () => {
                if (!userId || !subscriptionId) return
                const confirmedTypes: SubscriptionsType[] = []
                if (selectedLicense !== "none") confirmedTypes.push(selectedLicense as Licenses)
                Object.entries(selectedAddOns).forEach(([addOn, value]) => {
                    if (!!value) confirmedTypes.push(addOn as AddOns)
                })

                await Promise.all(
                    confirmedTypes.map(async (type) => {
                        await dispatch(
                            assignPurchasedLicense({
                                assigneeId: userId,
                                subscriptionId,
                                subscriptionType: type,
                            })
                        )
                    })
                )
                closeModal()
            },
        },
        [selectedLicense, selectedAddOns, subscriptionId, closeModal, dispatch, userId]
    )

    const licenses = licensesPurchased && Object.entries(licensesPurchased)
    const licenseRadioOptions = useMemo(() => {
        const options: { label: string; value: string }[] = []
        licenses?.forEach(([license]) => {
            options.push({
                label: formatSubscriptionType(license as SubscriptionsType, true),
                value: license,
            })
        })
        options.push({ label: "Don't apply to myself", value: "none" })
        return options
    }, [licenses])
    const addOns = addOnsPurchased && Object.entries(addOnsPurchased)
    return (
        <HighPriorityNovaModalContainer
            title="Assign Subscriptions to Yourself"
            icon={Icons.Key}
            className="w-[30rem]"
            bottomBar={
                <div className="w-full flex justify-end items-center gap-2">
                    <ButtonTertiary title="Skip for now" onClick={closeModal} />
                    <ButtonPrimary
                        title="Confirm"
                        onClick={handleConfirm}
                        isLoading={confirmLoading}
                    />
                </div>
            }
            closeModal={closeModal}
        >
            <>
                {isLoading && (
                    <div className="flex justify-center items-center ">
                        <NovaSpinner width={32} height={32} />
                    </div>
                )}
                {!isLoading && (
                    <>
                        <h5 className="mb-4">{t("You can always do this later.")}</h5>
                        {licenses && licenses.length > 0 && (
                            <div className="mb-8">
                                <h5>{t("Licenses")}</h5>
                                <small className="text-gray-300">
                                    {t("Select a license to apply to yourself.")}
                                </small>
                                <div className="mb-2.5" />
                                <RadioInput
                                    options={licenseRadioOptions}
                                    onChange={(checked) => setSelectedLicense(checked)}
                                    value={selectedLicense}
                                />
                            </div>
                        )}
                        {addOns && addOns.length > 0 && (
                            <>
                                <h5>{t("Add-ons")}</h5>
                                <small className="mb-2.5 text-gray-300">
                                    {t("Select one or more add-ons to apply to yourself.")}
                                </small>
                                <div className="mb-2.5" />
                                <div className="flex flex-col gap-3 ml-0.5">
                                    {addOns?.map(([addOn], idx) => {
                                        return (
                                            <CheckboxInput
                                                key={idx}
                                                id={addOn}
                                                onChange={(checked) =>
                                                    setSelectedAddOns((previousSelected) => ({
                                                        ...previousSelected,
                                                        [addOn]: checked,
                                                    }))
                                                }
                                                label={formatSubscriptionType(
                                                    addOn as SubscriptionsType,
                                                    true
                                                )}
                                                value={!!selectedAddOns[addOn as AddOns]}
                                                isDark
                                                isSmall
                                            />
                                        )
                                    })}
                                </div>
                            </>
                        )}
                    </>
                )}
            </>
        </HighPriorityNovaModalContainer>
    )
}
